import React from 'react';
import { graphql } from 'gatsby';

import Layout from '../layouts/Default';
import Billboard from '../components/Billboard';
import SEO from '../components/Seo';
import News from '../components/News';
import NewsList from '../components/CenterNews';
import { cleanMetatags, getAlternateLinks, dateToInternational } from '../ui/helpers';

interface IProps {
  data: any;
  pageContext: {
    id: string;
    translations: any;
    countryCode?: string;
    country?: string;
    backLink: string;
  };
}

const NewsPage = ({ data, pageContext }: IProps) => {
  const { nodeNewsPage } = data;
  let newsList = [];

  nodeNewsPage.relationships?.field_center?.relationships?.node__news_page
    .sort((a, b) => {
      a = dateToInternational(a.created, '/').split('/').join('');
      b = dateToInternational(b.created, '/').split('/').join('');
      return a <= b ? 1 : a > b ? -1 : 0;
    })
    .map(
      (news, i) =>
        news.id !== nodeNewsPage.id &&
        news.langcode === nodeNewsPage.langcode &&
        newsList.push({
          key: `news_${i}`,
          title: news.title,
          description: news.body.summary,
          date: news.created,
          link: {
            uri: `/${news.path?.langcode}${news?.path?.alias}`
          },
          imageUrl:
            news.relationships?.field_billboard_image.relationships?.field_media_image
              ?.drupal_links,
          imageAlt: news.relationships?.field_billboard_image.field_media_image.alt
        })
    );

  const regularMetatags = cleanMetatags(nodeNewsPage.metatag_normalized);
  const alternateLinks = getAlternateLinks(nodeNewsPage.metatag_normalized);

  let hrefLangs = [];

  nodeNewsPage.relationships?.field_hreflang &&
    nodeNewsPage.relationships.field_hreflang.map(element => {
      hrefLangs = hrefLangs.concat(getAlternateLinks(element.metatag_normalized));
    });

  const parsedDate = new Date(nodeNewsPage.created);
  let formattedDate = '';

  if (parsedDate instanceof Date && !isNaN(parsedDate)) {
    formattedDate = parsedDate.toLocaleDateString(nodeNewsPage.langcode, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  return (
    <Layout
      alternates={alternateLinks}
      lang={nodeNewsPage.langcode}
      country={pageContext.country}
      countryCode={pageContext.countryCode}
      translations={pageContext.translations}
      pageCta={nodeNewsPage?.field_cta}
      pageHref={nodeNewsPage?.path?.alias}>
      <SEO
        title={nodeNewsPage.title}
        lang={nodeNewsPage.langcode}
        metas={[...regularMetatags, ...alternateLinks, ...hrefLangs]}
      />
      <Billboard
        theme={'generic_gradient'}
        size={'billboard-small'}
        title={nodeNewsPage.title}
        date={formattedDate}
        breadcrumbs={{
          language: nodeNewsPage?.langcode,
          countryCode: pageContext.countryCode,
          pageHref: {
            alias: nodeNewsPage?.path?.alias,
            domain: nodeNewsPage?.relationships?.field_country?.field_domain?.uri
          }
        }}
      />
      <News
        translates={pageContext.translations}
        imageUrl={
          nodeNewsPage.relationships?.field_billboard_image?.relationships?.field_media_image
            ?.drupal_links
        }
        imageAlt={nodeNewsPage.field_billboard_image?.field_media_image?.alt}
        text={nodeNewsPage?.body?.processed}
      />
      {newsList && newsList.length > 0 && (
        <NewsList
          translations={pageContext.translations}
          news={newsList.slice(0, 3)}
          gtmAction="noticias"
          allNews={{ uri: pageContext.backLink, title: pageContext.translations.news.showAll }}
        />
      )}
    </Layout>
  );
};

export const query = graphql`
  query ($id: String) {
    nodeNewsPage(id: { eq: $id }) {
      id
      langcode
      status
      created(formatString: "MM/DD/YYYY")
      field_internal_title
      path {
        alias
        langcode
      }
      metatag_normalized {
        attributes {
          content
          href
          hreflang
          name
          property
          rel
        }
      }
      title
      drupal_id
      relationships {
        field_billboard_image {
          relationships {
            field_media_image {
              drupal_links {
                fullwidth {
                  href
                  meta {
                    height
                    width
                  }
                }
                large {
                  href
                  meta {
                    height
                    width
                  }
                }
                medium {
                  href
                  meta {
                    height
                    width
                  }
                }
                original {
                  href
                  meta {
                    height
                    width
                  }
                }
                small {
                  href
                  meta {
                    height
                    width
                  }
                }
                xlarge {
                  href
                  meta {
                    height
                    width
                  }
                }
                xsmall {
                  href
                  meta {
                    height
                    width
                  }
                }
                xxsmall {
                  href
                  meta {
                    height
                    width
                  }
                }
                fullwidth_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                large_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                medium_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                original_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                small_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                xlarge_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                xsmall_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
                xxsmall_webp {
                  href
                  meta {
                    height
                    width
                  }
                }
              }
            }
          }
        }
        field_center {
          title
          relationships {
            node__news_page {
              body {
                summary
              }
              created(formatString: "DD/MM/YYYY")
              path {
                alias
                langcode
              }
              id
              title
              langcode
              relationships {
                field_billboard_image {
                  relationships {
                    field_media_image {
                      drupal_links {
                        xsmall_webp {
                          href
                          meta {
                            height
                            width
                          }
                        }
                        xsmall {
                          href
                          meta {
                            height
                            width
                          }
                        }
                      }
                    }
                  }
                  field_media_image {
                    alt
                  }
                }
                field_country {
                  field_country_code
                  field_domain {
                    uri
                  }
                }
              }
            }
          }
        }
        field_country {
          field_domain {
            uri
          }
        }
      }
      body {
        processed
      }
    }
  }
`;

export default NewsPage;
